// frontend/src/pages/Reinsurance/components/CedentManagement/CedentCreate/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faSave, faTimes, 
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useCedent } from '../../../contexts/CedentContext';
import Input from '../../../../../components/form/Input/Input';
import Dropdown from '../../../../../components/form/Dropdown/Dropdown';
import TextArea from '../../../../../components/form/TextArea/TextArea';
import styles from './CedentCreate.module.css';

const CedentCreate = ({ onBack, onSuccess }) => {
  const { t } = useLanguage();
  const { createCedent, error: apiError, clearError } = useCedent();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Form data state
  const [formData, setFormData] = useState({
    cedent_name: '',
    cedent_domicile: '',
    cedent_type: '',
    cedent_rating: {
      am_best: '',
      sp: '',
      moodys: '',
      fitch: ''
    },
    contact_information: {
      address: {
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
      },
      primary_contact: {
        name: '',
        title: '',
        email: '',
        phone: ''
      }
    },
    financial_information: {
      currency: 'USD',
      annual_premium_volume: '',
      am_best_financial_size_category: ''
    },
    underwriting_information: {
      lines_of_business: [],
      territories: []
    },
    relationship_information: {
      notes: ''
    }
  });
  
  // Options for dropdowns
  const cedentTypeOptions = [
    { value: '', labelKey: 'select_cedent_type' },
    { value: 'Insurance Company', labelKey: 'insurance_company' },
    { value: 'Reinsurance Company', labelKey: 'reinsurance_company' },
    { value: 'Captive', labelKey: 'captive' },
    { value: 'Lloyd\'s Syndicate', labelKey: 'lloyds_syndicate' }
  ];
  
  const currencyOptions = [
    { value: 'USD', labelKey: 'usd' },
    { value: 'EUR', labelKey: 'eur' },
    { value: 'GBP', labelKey: 'gbp' },
    { value: 'JPY', labelKey: 'jpy' },
    { value: 'SGD', labelKey: 'sgd' },
    { value: 'CHF', labelKey: 'chf' }
  ];
  
  const financialSizeOptions = [
    { value: '', labelKey: 'select_financial_size' },
    { value: 'I', labelKey: 'size_i' },
    { value: 'II', labelKey: 'size_ii' },
    { value: 'III', labelKey: 'size_iii' },
    { value: 'IV', labelKey: 'size_iv' },
    { value: 'V', labelKey: 'size_v' },
    { value: 'VI', labelKey: 'size_vi' },
    { value: 'VII', labelKey: 'size_vii' },
    { value: 'VIII', labelKey: 'size_viii' },
    { value: 'IX', labelKey: 'size_ix' },
    { value: 'X', labelKey: 'size_x' },
    { value: 'XI', labelKey: 'size_xi' },
    { value: 'XII', labelKey: 'size_xii' },
    { value: 'XIII', labelKey: 'size_xiii' },
    { value: 'XIV', labelKey: 'size_xiv' },
    { value: 'XV', labelKey: 'size_xv' }
  ];
  
  const linesOfBusinessOptions = [
    { value: 'Property', labelKey: 'property' },
    { value: 'Casualty', labelKey: 'casualty' },
    { value: 'Marine', labelKey: 'marine' },
    { value: 'Aviation', labelKey: 'aviation' },
    { value: 'Energy', labelKey: 'energy' },
    { value: 'Engineering', labelKey: 'engineering' },
    { value: 'Specialty', labelKey: 'specialty' },
    { value: 'Cyber', labelKey: 'cyber' },
    { value: 'Life', labelKey: 'life' },
    { value: 'Health', labelKey: 'health' }
  ];
  
  const territoryOptions = [
    { value: 'North America', labelKey: 'north_america' },
    { value: 'Europe', labelKey: 'europe' },
    { value: 'Asia Pacific', labelKey: 'asia_pacific' },
    { value: 'Latin America', labelKey: 'latin_america' },
    { value: 'Middle East', labelKey: 'middle_east' },
    { value: 'Africa', labelKey: 'africa' },
    { value: 'Global', labelKey: 'global' }
  ];
  
  // Handle form field changes
  const handleChange = (field, value) => {
    // Handle nested properties
    if (field.includes('.')) {
      const parts = field.split('.');
      
      if (parts.length === 2) {
        setFormData(prev => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: value
          }
        }));
      } else if (parts.length === 3) {
        setFormData(prev => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: {
              ...prev[parts[0]][parts[1]],
              [parts[2]]: value
            }
          }
        }));
      } else if (parts.length === 4) {
        setFormData(prev => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: {
              ...prev[parts[0]][parts[1]],
              [parts[2]]: {
                ...prev[parts[0]][parts[1]][parts[2]],
                [parts[3]]: value
              }
            }
          }
        }));
      }
    } else {
      // Handle simple properties
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    if (!formData.cedent_name) return 'cedent_name_required';
    if (!formData.cedent_domicile) return 'cedent_domicile_required';
    if (!formData.cedent_type) return 'cedent_type_required';
    
    // Validate email format if provided
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.contact_information.primary_contact.email && 
        !emailRegex.test(formData.contact_information.primary_contact.email)) {
      return 'invalid_email_format';
    }
    
    return null;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    clearError();
    
    try {
      // Format data for API
      const cedentData = {
        ...formData,
        // Convert string values to numbers where needed
        financial_information: {
          ...formData.financial_information,
          annual_premium_volume: formData.financial_information.annual_premium_volume 
            ? parseFloat(formData.financial_information.annual_premium_volume) 
            : null
        }
      };
      
      const response = await createCedent(cedentData);
      
      if (response.success) {
        onSuccess(response.data.cedent_id);
      } else {
        throw new Error(response.error || 'Failed to create cedent');
      }
    } catch (err) {
      console.error('Error creating cedent:', err);
      setFormError(err.message || 'Failed to create cedent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className={styles.cedentCreate}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('back_to_cedents')}</span>
        </button>
        <h1 className={styles.title}>{t('create_cedent')}</h1>
      </div>
      
      {(formError || apiError) && (
        <div className={styles.error}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{t(formError) || apiError}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formContent}>
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('basic_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="cedent_name"
                  name="cedent_name"
                  labelKey="cedent_name"
                  placeholderKey="enter_cedent_name"
                  value={formData.cedent_name}
                  onChange={(value) => handleChange('cedent_name', value)}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="cedent_domicile"
                  name="cedent_domicile"
                  labelKey="cedent_domicile"
                  placeholderKey="enter_cedent_domicile"
                  value={formData.cedent_domicile}
                  onChange={(value) => handleChange('cedent_domicile', value)}
                  required
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="cedent_type"
                  labelKey="cedent_type"
                  options={cedentTypeOptions}
                  value={formData.cedent_type}
                  onChange={(value) => handleChange('cedent_type', value)}
                  required
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('ratings')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="am_best"
                  name="am_best"
                  labelKey="am_best_rating"
                  placeholderKey="enter_am_best_rating"
                  value={formData.cedent_rating.am_best}
                  onChange={(value) => handleChange('cedent_rating.am_best', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="sp"
                  name="sp"
                  labelKey="sp_rating"
                  placeholderKey="enter_sp_rating"
                  value={formData.cedent_rating.sp}
                  onChange={(value) => handleChange('cedent_rating.sp', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="moodys"
                  name="moodys"
                  labelKey="moodys_rating"
                  placeholderKey="enter_moodys_rating"
                  value={formData.cedent_rating.moodys}
                  onChange={(value) => handleChange('cedent_rating.moodys', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="fitch"
                  name="fitch"
                  labelKey="fitch_rating"
                  placeholderKey="enter_fitch_rating"
                  value={formData.cedent_rating.fitch}
                  onChange={(value) => handleChange('cedent_rating.fitch', value)}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('contact_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="contact_name"
                  name="contact_name"
                  labelKey="primary_contact_name"
                  placeholderKey="enter_contact_name"
                  value={formData.contact_information.primary_contact.name}
                  onChange={(value) => handleChange('contact_information.primary_contact.name', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="contact_title"
                  name="contact_title"
                  labelKey="primary_contact_title"
                  placeholderKey="enter_contact_title"
                  value={formData.contact_information.primary_contact.title}
                  onChange={(value) => handleChange('contact_information.primary_contact.title', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="contact_email"
                  name="contact_email"
                  labelKey="primary_contact_email"
                  placeholderKey="enter_contact_email"
                  value={formData.contact_information.primary_contact.email}
                  onChange={(value) => handleChange('contact_information.primary_contact.email', value)}
                  type="email"
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="contact_phone"
                  name="contact_phone"
                  labelKey="primary_contact_phone"
                  placeholderKey="enter_contact_phone"
                  value={formData.contact_information.primary_contact.phone}
                  onChange={(value) => handleChange('contact_information.primary_contact.phone', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="address_street"
                  name="address_street"
                  labelKey="street_address"
                  placeholderKey="enter_street_address"
                  value={formData.contact_information.address.street}
                  onChange={(value) => handleChange('contact_information.address.street', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="address_city"
                  name="address_city"
                  labelKey="city"
                  placeholderKey="enter_city"
                  value={formData.contact_information.address.city}
                  onChange={(value) => handleChange('contact_information.address.city', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="address_state"
                  name="address_state"
                  labelKey="state_province"
                  placeholderKey="enter_state_province"
                  value={formData.contact_information.address.state}
                  onChange={(value) => handleChange('contact_information.address.state', value)}
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  id="address_postal_code"
                  name="address_postal_code"
                  labelKey="postal_code"
                  placeholderKey="enter_postal_code"
                  value={formData.contact_information.address.postal_code}
                  onChange={(value) => handleChange('contact_information.address.postal_code', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="address_country"
                  name="address_country"
                  labelKey="country"
                  placeholderKey="enter_country"
                  value={formData.contact_information.address.country}
                  onChange={(value) => handleChange('contact_information.address.country', value)}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('financial_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="currency"
                  labelKey="currency"
                  options={currencyOptions}
                  value={formData.financial_information.currency}
                  onChange={(value) => handleChange('financial_information.currency', value)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <Input
                  id="annual_premium_volume"
                  name="annual_premium_volume"
                  labelKey="annual_premium_volume"
                  placeholderKey="enter_annual_premium_volume"
                  value={formData.financial_information.annual_premium_volume}
                  onChange={(value) => handleChange('financial_information.annual_premium_volume', value)}
                  type="number"
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="am_best_financial_size_category"
                  labelKey="am_best_financial_size_category"
                  options={financialSizeOptions}
                  value={formData.financial_information.am_best_financial_size_category}
                  onChange={(value) => handleChange('financial_information.am_best_financial_size_category', value)}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('underwriting_information')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="lines_of_business"
                  labelKey="lines_of_business"
                  options={linesOfBusinessOptions}
                  value={formData.underwriting_information.lines_of_business}
                  onChange={(value) => handleChange('underwriting_information.lines_of_business', value)}
                  multiple
                />
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Dropdown
                  id="territories"
                  labelKey="territories"
                  options={territoryOptions}
                  value={formData.underwriting_information.territories}
                  onChange={(value) => handleChange('underwriting_information.territories', value)}
                  multiple
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>{t('relationship_notes')}</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <TextArea
                  id="relationship_notes"
                  labelKey="notes"
                  placeholderKey="enter_relationship_notes"
                  value={formData.relationship_information.notes}
                  onChange={(value) => handleChange('relationship_information.notes', value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.formActions}>
          <button 
            type="button" 
            className={styles.cancelButton} 
            onClick={onBack}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span>{t('cancel')}</span>
          </button>
          
          <button 
            type="submit" 
            className={styles.saveButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className={styles.spinner}></div>
                <span>{t('saving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span>{t('save_cedent')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CedentCreate;